import { View, Pressable } from "react-native"
import { Alert } from "./ui/confirm"
import Icon from "@react-native-vector-icons/material-design-icons"
import { useForm, Controller, useFieldArray } from "react-hook-form"
import { router } from "expo-router"
import { zodResolver } from "@hookform/resolvers/zod"

import { createCustomerInput } from "@white-labels/input/src/m5azen"
import { KeyboardAwareScrollView, Button, Text, TextInput } from "@src/components"
import { sharedStyles, trpc } from "@src/utils"
import { ar } from "@src/i18n/ar"

type Props = { id?: number }

export function CustomerForm({ id }: Props) {
  const utils = trpc.useUtils()
  const customer = trpc.customers.get.useQuery({ id: id as number }, { enabled: id !== undefined })
  const addCustomer = trpc.customers.add.useMutation()
  const editCustomer = trpc.customers.edit.useMutation()

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(createCustomerInput),
    defaultValues: { name: customer.data?.name, phoneNumbers: customer.data?.phoneNumbers },
  })

  const { fields, append, remove } = useFieldArray({ control, name: "phoneNumbers" })

  const handleFormSubmit = handleSubmit(async (data) => {
    const hasPrimary = data.phoneNumbers?.some((p) => p.primary)
    if (!hasPrimary && data.phoneNumbers?.[0]) data.phoneNumbers[0].primary = true

    try {
      if (id) {
        await editCustomer.mutateAsync({ id, name: data.name, phoneNumbers: data.phoneNumbers })
        await utils.customers.get.invalidate({ id })
      } else {
        await addCustomer.mutateAsync({ name: data.name, phoneNumbers: data.phoneNumbers })
      }
      await utils.customers.list.invalidate()
      router.back()
    } catch (error) {
      // @ts-expect-error
      Alert.alert("Error", error.message)
    }
  })

  const addPhoneNumber = () => {
    append({ phone: "", primary: false })
  }

  const removePhoneNumber = (index: number) => {
    remove(index)
  }

  const { errors, isValid } = formState
  const loading = addCustomer.status === "pending" || editCustomer.status === "pending"
  const disabled = !isValid || loading

  return (
    <KeyboardAwareScrollView className="flex-1 bg-gray-50" contentContainerStyle={sharedStyles.contentContainer}>
      <Text className="text-base font-semibold text-gray-800 mb-2 text-left">{ar.name}</Text>
      <Controller
        control={control}
        name="name"
        render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} className="mb-4" placeholder={ar.name} />}
      />
      {errors.name && <Text className="text-red-500 mb-2">{errors.name.message}</Text>}

      <View className="mb-6">
        <Text className="text-base font-semibold text-gray-800 mb-2 text-left">{ar.phoneNumbers}</Text>
        {fields.map((field, index) => (
          <View key={field.id} className="mb-3 flex-row items-center gap-2">
            <View className="flex-1">
              <Controller
                control={control}
                name={`phoneNumbers.${index}.phone`}
                render={({ field }) => (
                  <TextInput value={field.value} onChangeText={field.onChange} placeholder={ar.enterPhoneNumber} keyboardType="phone-pad" />
                )}
              />
            </View>

            {/* <Pressable onPress={() => togglePrimary(index)} className={`p-2 rounded ${fields[index].primary ? "bg-blue-600" : "bg-gray-300"}`}>
              <Icon name="star" size={20} color={fields[index].primary ? "#fff" : "#666"} />
            </Pressable> */}

            <Pressable onPress={() => removePhoneNumber(index)} className="p-2 rounded bg-red-100">
              <Icon name="delete" size={20} color="#EF4444" />
            </Pressable>
          </View>
        ))}

        <Pressable onPress={addPhoneNumber} className="flex-row items-center justify-center bg-blue-100 border-2 border-blue-300 rounded py-2 mt-2">
          <Icon name="plus" size={20} color="#3B82F6" />
          <Text className="text-blue-600 ml-2 font-medium text-left">{ar.addPhoneNumber}</Text>
        </Pressable>
      </View>

      <Button loading={loading} onPress={handleFormSubmit} disabled={disabled}>
        {ar.confirm}
      </Button>
    </KeyboardAwareScrollView>
  )
}
